import {useEffect, useRef} from "react";
import { useTranslation } from 'react-i18next';
import ProductShippingStatus from "./ProductShippingStatus";

/**
 * Extracts plain sentences from hitState.status_information.
 */
function extractStatusSentences(html: string): string[] {
  return (
    html
      .replace(/<[^>]+>/g, "")
      .match(/[^.?!]+[.?!]/g)
      ?.map((s) => s.trim()) || []
  );
}

interface ProductShippingStatusPopperProps {
  statusNumber: number;
  statusText: string;
  statusInformation?: string;
  productIndicator?: string;
  prescriptionRequired?: boolean;
  onClose: () => void;
}

export default function ProductShippingStatusPopper({
                                                      statusNumber,
                                                      statusText,
                                                      statusInformation,
                                                      productIndicator,
                                                      prescriptionRequired,
                                                      onClose,
                                                    }: ProductShippingStatusPopperProps) {
  const { t } = useTranslation();
  // Build paragraphs
  const sentences = extractStatusSentences(statusInformation || "");
  const popperParagraphs =
    sentences.length > 1
      ? [sentences[0], sentences.slice(1).join(" ")]
      : sentences;

  const popperRef = useRef<HTMLDivElement>(null);

  // Keep popper inside viewport horizontally
  useEffect(() => {
    const popperElement = popperRef.current;
    if (!popperElement) return;

    const repositionPopper = () => {
      popperElement.style.left = "0px";
      const popperRect = popperElement.getBoundingClientRect();
      const viewportPadding = 16;
      let horizontalOffset = 0;

      if (popperRect.right > window.innerWidth - viewportPadding) {
        horizontalOffset =
          window.innerWidth - viewportPadding - popperRect.right;
      } else if (popperRect.left < viewportPadding) {
        horizontalOffset = viewportPadding - popperRect.left;
      }

      if (horizontalOffset !== 0) {
        popperElement.style.left = `${horizontalOffset}px`;
      }
    };

    repositionPopper();
    window.addEventListener("resize", repositionPopper);
    return () => window.removeEventListener("resize", repositionPopper);
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popperRef.current && !popperRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={popperRef}
      className="product-card__availability-popper"
      role="dialog"
      aria-modal="true"
      aria-labelledby="availability-title"
    >
      <div className="product-card__availability-popper-container">

        <header className="product-card__availability-popper-header availability-wrap">
          <h2
            id="availability-title"
            className="product-card__availability-popper-heading"
          >
            <ProductShippingStatus
              statusNumber={statusNumber}
              statusText={statusText}
              productIndicator={productIndicator}
              prescriptionRequired={prescriptionRequired}
              disablePopper
            />
          </h2>
          <button
            className="product-card__availability-popper-close"
            onClick={onClose}
            aria-label={t('search.hits.closePopup') as string}
            type="button"
          >
            <svg className="icon" width="10" height="10" aria-hidden="true">
              <use href="#icon_close"/>
            </svg>
          </button>
        </header>

        <div
          className="product-card__availability-popper-body"
          role="status"
        >
          {popperParagraphs.map((sentence, idx) => (
            <p key={idx}>{sentence}</p>
          ))}
        </div>
      </div>
    </div>
  );
}